import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollText } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import DataTable from '@/components/ui/DataTable';
import Badge from '@/components/ui/Badge';
import Select from '@/components/ui/Select';
import Toolbar from '@/components/ui/Toolbar';
import EmptyState from '@/components/ui/EmptyState';

const fmtWhen = (d) =>
  new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

// Resumo compacto de before→after (só as chaves que mudaram).
const diff = (log) => {
  const parts = [];
  const before = log.before || {};
  const after = log.after || {};
  for (const k of new Set([...Object.keys(before), ...Object.keys(after)])) {
    if (before[k] !== after[k]) parts.push(`${k}: ${before[k] ?? '—'} → ${after[k] ?? '—'}`);
  }
  if (!parts.length && log.meta) {
    parts.push(Object.entries(log.meta).map(([k, v]) => `${k}: ${v}`).join(' · '));
  }
  return parts.join(' · ');
};

/** Trilha de auditoria: quem fez o quê nas ações sensíveis da plataforma. */
export default function AdminAudit() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');

  useEffect(() => {
    setLoading(true);
    api
      .get('/admin/audit-logs', action ? { action } : {})
      .then((res) => setRows((res.data || res).logs || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [action]);

  const actions = [...new Set(rows.map((r) => r.action))];

  const columns = [
    { key: 'createdAt', header: t('audit.colWhen'), render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmtWhen(r.createdAt)}</span> },
    {
      key: 'actor',
      header: t('audit.colActor'),
      render: (r) => (
        <span>
          <strong style={{ color: 'var(--hi)' }}>{r.actorName || '—'}</strong>
          {r.actorRole && <span style={{ color: 'var(--low)', fontSize: 11, marginLeft: 6 }}>{r.actorRole}</span>}
        </span>
      ),
    },
    { key: 'action', header: t('audit.colAction'), render: (r) => <Badge variant="accent">{r.action}</Badge> },
    {
      key: 'target',
      header: t('audit.colTarget'),
      render: (r) => (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--mid)' }}>
          {r.targetType ? `${r.targetType}#${r.targetId ?? '—'}` : '—'}
        </span>
      ),
    },
    { key: 'detail', header: t('audit.colDetail'), render: (r) => <span style={{ color: 'var(--mid)', fontSize: 12.5 }}>{diff(r) || '—'}</span> },
  ];

  return (
    <>
      <PageHeader kicker="Movez · Admin" title={t('nav.audit')} />

      <Toolbar>
        <Select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          options={[{ value: '', label: t('audit.allActions') }, ...actions.map((a) => ({ value: a, label: a }))]}
        />
      </Toolbar>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        empty={<EmptyState icon={ScrollText} title={t('audit.emptyTitle')} description={t('audit.emptyDesc')} />}
      />
    </>
  );
}
